import React, { FC, PropsWithChildren, useEffect, useState } from 'react'
import { Box, Grid, Paper, Typography } from '@mui/material'
import { DataGridPremium, GridPaginationModel, useGridApiRef } from '@mui/x-data-grid-premium'
import { SensorBatteryStats, SensorState } from 'src/api'
import { TabGroupFilter, PropsInjector, DataLoad } from 'src/components'
import { useSensorBatteryStatsListRequest, useSensorStatesRequest } from 'src/hooks/api'
import { useQueryParams, useQueryParamsState } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'
import { SensorBatteryStatsListColumns } from 'src/components/columns/sensorBatteryStatsList'
import { BatteryStatsListFilters } from 'src/components/forms/sensor/batteryStatsListFilters'
import { BatteryLifeDistributionChart } from './batteryLifeDistributionChart'

const defaultPerPage = 25
const defaultPage = 1
const initialQueryParams = {
  page: `${defaultPage}`,
  rowsPerPage: `${defaultPerPage}`
}

const transformStateIds = () => (searchParams: Record<string, any>) => {
  const state = searchParams[SearchParamsKeys.State]
  const params = { ...searchParams }
  delete params[SearchParamsKeys.State]
  if (state === undefined || state === '') return params
  const ids = state
    .split(',')
    .map((v: string) => v.trim())
    .filter(Boolean)
  if (ids.length > 0) {
    params.stateId = ids.join(',')
  }
  return params
}

const columns = [
  SensorBatteryStatsListColumns.IMEI,
  SensorBatteryStatsListColumns.BatteryStartValue,
  SensorBatteryStatsListColumns.DaysSinceLastCharge,
  SensorBatteryStatsListColumns.CurrentBattery,
  SensorBatteryStatsListColumns.DailyDischargeRate,
  SensorBatteryStatsListColumns.MaxHourlyDeviation,
  SensorBatteryStatsListColumns.EstimatedDaysRemaining,
  SensorBatteryStatsListColumns.BleConfiguration,
  SensorBatteryStatsListColumns.LocationConfiguration,
  SensorBatteryStatsListColumns.TemperatureConfiguration,
  SensorBatteryStatsListColumns.HumidityConfiguration,
  SensorBatteryStatsListColumns.PressureConfiguration,
  SensorBatteryStatsListColumns.LightConfiguration,
  SensorBatteryStatsListColumns.AccelerometerConfiguration,
  SensorBatteryStatsListColumns.GyroConfiguration,
  SensorBatteryStatsListColumns.BatteryConfiguration,
  SensorBatteryStatsListColumns.SignalStrengthConfiguration
]

interface BatteryStatsTabContentProps {
  tableData?: SensorBatteryStats[]
  tableCount?: number
  tableIsLoading?: boolean
  chartData?: SensorBatteryStats[]
  chartIsLoading?: boolean
  states?: SensorState[]
}

const BatteryStatsTabContent: FC<BatteryStatsTabContentProps> = ({
  tableData = [],
  tableCount = 0,
  tableIsLoading = false,
  chartData = [],
  chartIsLoading = false,
  states = []
}) => {
  const apiRef = useGridApiRef()
  const [queryParams, setQueryParams] = useQueryParams(initialQueryParams)
  const [rowCountState, setRowCountState] = useState(tableCount)

  useEffect(() => {
    setRowCountState((prev) => (tableCount !== undefined ? tableCount : prev))
  }, [tableCount])

  const page = queryParams['page'] ? parseInt(queryParams['page'], 10) - 1 : defaultPage - 1
  const pageSize = queryParams['rowsPerPage'] ? parseInt(queryParams['rowsPerPage'], 10) : defaultPerPage

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const newPage = pagination.page + 1
    setQueryParams(
      {
        [SearchParamsKeys.Page]: `${newPage}`,
        [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`
      },
      [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
    )
  }

  return (
    <>
      <Box mb={3}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Battery life distribution
        </Typography>
        <BatteryLifeDistributionChart data={chartData} isLoading={chartIsLoading} />
      </Box>
      <Grid container item flexDirection="column" justifyContent="space-between" mb={2}>
        <Grid item xs={12}>
          <BatteryStatsListFilters states={states} />
        </Grid>
      </Grid>
      <Paper sx={{ flexGrow: 1, minHeight: '200px', height: '100%', maxHeight: '800px' }}>
        <DataGridPremium<SensorBatteryStats>
          apiRef={apiRef}
          pagination
          disableRowSelectionOnClick
          rows={tableData}
          columns={columns}
          rowCount={rowCountState}
          loading={tableIsLoading}
          paginationModel={{
            page: page as number,
            pageSize: pageSize as number
          }}
          rowThreshold={2}
          columnThreshold={2}
          paginationMode="server"
          pageSizeOptions={defaultRowsValuePerPage}
          onPaginationModelChange={handleChangePaginationModel}
          getRowId={(row) => row.imei || Math.random().toString()}
          localeText={{
            columnMenuSortAsc: 'Sort A-Z',
            columnMenuSortDesc: 'Sort Z-A'
          }}
          sx={(theme) => ({
            border: 'none',
            '& .MuiDataGrid-columnHeaderTitleContainerContent': {
              paddingLeft: '5px',
              overflow: 'visible'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: 'rgba(0, 0, 0, 0.6)',
              overflow: 'visible'
            }
          })}
        />
      </Paper>
    </>
  )
}

interface SensorBatteryStatsDataLoaderProps {
  states: SensorState[]
}

const SensorBatteryStatsDataLoader: FC<PropsWithChildren<SensorBatteryStatsDataLoaderProps>> = ({ states, children }) => {
  const queryParamsState = useQueryParamsState()

  const chartRequest = useSensorBatteryStatsListRequest({
    params: {
      limit: 10000,
      offset: 0
    }
  })

  const tableRequest = useSensorBatteryStatsListRequest({
    params: {
      ...queryParamsState
    },
    paramsTransform: [transformStateIds()]
  })

  const props = {
    chartData: chartRequest.data?.rows || [],
    chartIsLoading: chartRequest.isInitialLoading || chartRequest.isLoading,
    tableData: tableRequest.data?.rows || [],
    tableCount: tableRequest.data?.count || 0,
    tableIsLoading: tableRequest.isInitialLoading || tableRequest.isLoading,
    states
  }

  const isError = chartRequest.isError || tableRequest.isError
  const errorMessage = chartRequest.error?.message || tableRequest.error?.message

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={errorMessage}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

const getStatusList = ({
  tableData,
  tableCount,
  tableIsLoading,
  chartData,
  chartIsLoading,
  states
}: {
  tableData: SensorBatteryStats[]
  tableCount: number
  tableIsLoading: boolean
  chartData: SensorBatteryStats[]
  chartIsLoading: boolean
  states: SensorState[]
}) => [
  {
    status: 'Battery life distribution',
    content: (
      <BatteryStatsTabContent tableData={tableData} tableCount={tableCount} tableIsLoading={tableIsLoading} chartData={chartData} chartIsLoading={chartIsLoading} states={states} />
    )
  }
]

interface SensorBatteryStatsTabsProps {
  tableData?: SensorBatteryStats[]
  tableCount?: number
  tableIsLoading?: boolean
  chartData?: SensorBatteryStats[]
  chartIsLoading?: boolean
  states?: SensorState[]
}

const SensorBatteryStatsTabs: FC<SensorBatteryStatsTabsProps> = ({
  tableData = [],
  tableCount = 0,
  tableIsLoading = false,
  chartData = [],
  chartIsLoading = false,
  states = []
}) => {
  return <TabGroupFilter statusList={getStatusList({ tableData, tableCount, tableIsLoading, chartData, chartIsLoading, states })} name={SearchParamsKeys.Tab} />
}

const SensorStateLoader: FC<PropsWithChildren> = ({ children }) => {
  const { data, isInitialLoading, isError } = useSensorStatesRequest()
  return (
    <DataLoad isLoading={isInitialLoading} isError={isError}>
      <PropsInjector props={{ states: data || [] }}>{children}</PropsInjector>
    </DataLoad>
  )
}

interface SensorBatteryStatsListProps {
  states?: SensorState[]
}

const SensorBatteryStatsListInner: FC<SensorBatteryStatsListProps> = ({ states = [] }) => {
  return (
    <SensorBatteryStatsDataLoader states={states}>
      <SensorBatteryStatsTabs />
    </SensorBatteryStatsDataLoader>
  )
}

export const SensorBatteryStatsList: FC = () => {
  return (
    <SensorStateLoader>
      <SensorBatteryStatsListInner />
    </SensorStateLoader>
  )
}
